/*
 * PacZel — a custom Pacman-style game for ZelTechnology members.
 * Pure HTML5 canvas + vanilla JS. No external assets or libraries.
 * Classic mechanics: maze, pellets, power pellets, 4 ghosts,
 * frightened mode, lives, wrap-around tunnels.
 */

export const GAME_NAME = "pacman";

const TILE = 20; // px per tile

// 0 empty, 1 wall, 2 pellet, 3 power pellet
const MAZE = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,1],
  [1,3,1,1,2,1,1,1,2,1,2,1,1,1,2,1,1,3,1],
  [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
  [1,2,1,1,2,1,2,1,1,1,1,1,2,1,2,1,1,2,1],
  [1,2,2,2,2,1,2,2,2,1,2,2,2,1,2,2,2,2,1],
  [1,1,1,2,1,1,1,1,2,1,2,1,1,1,1,2,1,1,1],
  [1,0,1,2,2,2,2,2,2,2,2,2,2,2,2,2,1,0,1],
  [1,1,1,2,1,2,1,1,1,1,1,1,1,2,1,2,1,1,1],
  [1,2,2,2,1,2,2,2,1,1,2,2,2,2,1,2,2,2,1],
  [1,2,1,1,1,1,1,0,0,0,0,1,1,1,1,1,1,2,1],
  [1,2,1,0,0,0,0,0,0,0,0,0,0,0,0,1,1,2,1],
  [1,2,1,0,1,1,1,1,1,1,1,1,1,1,0,1,1,2,1],
  [1,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,1],
  [1,2,1,1,1,1,1,0,0,0,0,1,1,1,1,1,1,2,1],
  [1,2,1,0,0,0,0,0,0,0,0,0,0,0,0,1,1,2,1],
  [1,2,1,0,1,1,1,1,1,1,1,1,1,1,0,1,1,2,1],
  [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
  [1,3,1,1,2,1,1,1,2,1,2,1,1,1,2,1,1,3,1],
  [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const ROWS = MAZE.length;
const COLS = MAZE[0].length;
const WIDTH = COLS * TILE;
const HEIGHT = ROWS * TILE;

const COLORS = {
  bg: "#0f172a",
  wall: "#1e3a8a",
  pellet: "#9198e5",
  power: "#12bfbe",
  player: "#2563eb",
  ghost: ["#ef4444", "#f59e0b", "#ec4899", "#22c55e"],
  frightened: "#6b7280",
};

const DIRS = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

const PLAYER_START = { x: 8, y: 13 }; // open corridor, safely away from the ghost house
const GHOST_START = { x: 9, y: 10 }; // ghost house pocket

export function createGame(canvas, { onScore = () => {}, onGameOver = () => {} } = {}) {
  const ctx = canvas.getContext("2d");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;

  let grid, player, ghosts, score, lives, level, pelletsLeft, running, frightTimer;
  let raf = null;
  let frame = 0;
  const PLAYER_INTERVAL = 11; // player steps every N frames (~5.5 tiles/sec — easygoing pace)
  const GHOST_INTERVAL = 22;  // ghosts are half your speed, giving thinking room
  const FRIGHT_FRAMES = 300;  // ~5 seconds of frightened mode

  function wrapX(x) {
    return ((x % COLS) + COLS) % COLS;
  }

  function resetState(keepScore) {
    if (!keepScore) score = 0;
    grid = MAZE.map((row) => row.slice());
    player = {
      x: PLAYER_START.x, y: PLAYER_START.y,
      drawX: PLAYER_START.x, drawY: PLAYER_START.y, // smooth pixel position
      dir: "left", next: null, mouth: 0,
    };
    ghosts = [0, 1, 2, 3].map((i) => ({
      x: GHOST_START.x,
      y: GHOST_START.y,
      drawX: GHOST_START.x, drawY: GHOST_START.y,
      dir: "up",
      mode: "house", // in the ghost house: harmless, waits for release
      releaseAt: frame + (i + 1) * 150, // staggered exit — ghosts leave one by one (relative to now)
    }));
    lives = 3;
    level = 1;
    frightTimer = 0;
    countPellets();
  }

  function countPellets() {
    pelletsLeft = 0;
    for (const row of grid) for (const c of row) if (c === 2 || c === 3) pelletsLeft++;
  }

  function isWall(x, y) {
    if (y < 0 || y >= ROWS) return true;
    return grid[y][wrapX(x)] === 1;
  }

  function stepPlayer() {
    if (player.next && !isWall(player.x + DIRS[player.next].x, player.y + DIRS[player.next].y)) {
      player.dir = player.next;
      player.next = null;
    }
    const d = DIRS[player.dir];
    if (!isWall(player.x + d.x, player.y + d.y)) {
      player.x = wrapX(player.x + d.x);
      player.y += d.y;
      player.mouth = (player.mouth || 0) + 1;
    }

    const cell = grid[player.y] && grid[player.y][player.x];
    if (cell === 2) {
      grid[player.y][player.x] = 0;
      pelletsLeft--;
      score += 10;
      onScore(score);
    } else if (cell === 3) {
      grid[player.y][player.x] = 0;
      pelletsLeft--;
      score += 50;
      frightTimer = FRIGHT_FRAMES;
      ghosts.forEach((g) => { g.mode = "frightened"; g.frightSteps = Math.floor(FRIGHT_FRAMES / GHOST_INTERVAL); });
      onScore(score);
    }
  }

  function stepGhost(g, idx) {
    // ghosts wait in the house until their release time
    if (g.mode === "house") {
      if (frame >= g.releaseAt) g.mode = "chase";
      return;
    }
    if (g.frightSteps > 0) {
      g.frightSteps--;
      if (g.frightSteps === 0) g.mode = "chase";
    }

    // directions that are legal and don't reverse
    const options = Object.entries(DIRS).filter(([d, v]) => {
      const opposite = { up: "down", down: "up", left: "right", right: "left" };
      if (d === opposite[g.dir]) return false;
      return !isWall(g.x + v.x, g.y + v.y);
    });
    if (options.length === 0) return;

    let best;
    if (g.mode === "frightened") {
      best = options[Math.floor(Math.random() * options.length)];
    } else {
      let bestDist = Infinity;
      for (const opt of options) {
        const nx = g.x + opt[1].x;
        const ny = g.y + opt[1].y;
        const dist = Math.abs(nx - player.x) + Math.abs(ny - player.y);
        if (dist < bestDist) { bestDist = dist; best = opt; }
      }
    }

    g.dir = best[0];
    g.x = wrapX(g.x + best[1].x);
    g.y += best[1].y;
  }

  function checkCollisions() {
    for (const g of ghosts) {
      if (g.mode === "house") continue;
      if (g.x === player.x && g.y === player.y) {
        if (g.mode === "frightened") {
          g.x = GHOST_START.x; g.y = GHOST_START.y;
          g.mode = "house";
          g.releaseAt = frame + 120; // eaten ghost returns to house, then re-joins
          g.frightSteps = 0;
          score += 200;
          onScore(score);
        } else {
          lives--;
          if (lives <= 0) {
            running = false;
            onGameOver(score);
          } else {
            player.x = PLAYER_START.x; player.y = PLAYER_START.y; player.dir = "left";
            ghosts.forEach((gh, i) => {
              gh.x = GHOST_START.x; gh.y = GHOST_START.y;
              gh.mode = "house";
              gh.releaseAt = frame + (i + 1) * 150; // re-stagger after respawn
              gh.frightSteps = 0;
            });
          }
        }
      }
    }
  }

  // move the visual position toward the grid position at constant px/frame speed
  function smooth(cur, target, speed) {
    if (Math.abs(target - cur) > 2) return target; // wrap-around tunnel: snap
    if (target > cur) return Math.min(target, cur + speed);
    if (target < cur) return Math.max(target, cur - speed);
    return cur;
  }

  function smoothEntities() {
    const pSpeed = TILE / PLAYER_INTERVAL;
    player.drawX = smooth(player.drawX, player.x, pSpeed);
    player.drawY = smooth(player.drawY, player.y, pSpeed);
    const gSpeed = TILE / GHOST_INTERVAL;
    for (const g of ghosts) {
      g.drawX = smooth(g.drawX, g.x, gSpeed);
      g.drawY = smooth(g.drawY, g.y, gSpeed);
    }
  }

  function update() {
    if (!running) return;
    frame++;
    if (frame % PLAYER_INTERVAL === 0) stepPlayer();
    if (frame % GHOST_INTERVAL === 0) ghosts.forEach(stepGhost);
    if (frightTimer > 0) {
      frightTimer--;
    }
    smoothEntities();
    checkCollisions();
    if (pelletsLeft <= 0) {
      resetState(true); // next level, keep score
    }
  }

  function draw() {
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        const c = grid[y][x];
        if (c === 1) {
          ctx.fillStyle = COLORS.wall;
          ctx.fillRect(x * TILE, y * TILE, TILE - 1, TILE - 1);
        } else if (c === 2) {
          ctx.fillStyle = COLORS.pellet;
          ctx.beginPath();
          ctx.arc(x * TILE + TILE / 2, y * TILE + TILE / 2, 3, 0, Math.PI * 2);
          ctx.fill();
        } else if (c === 3) {
          ctx.fillStyle = COLORS.power;
          ctx.beginPath();
          ctx.arc(x * TILE + TILE / 2, y * TILE + TILE / 2, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // player — animated mouth, smooth pixel movement
    const px = player.drawX * TILE + TILE / 2;
    const py = player.drawY * TILE + TILE / 2;
    const mouth = 0.15 + Math.abs(Math.sin((player.mouth || 0) * 0.25)) * 0.4;
    ctx.fillStyle = COLORS.player;
    ctx.beginPath();
    const base = { right: 0, down: Math.PI / 2, left: Math.PI, up: -Math.PI / 2 }[player.dir];
    ctx.moveTo(px, py);
    ctx.arc(px, py, TILE / 2 - 2, base + mouth, base - mouth + Math.PI * 2);
    ctx.closePath();
    ctx.fill();

    // ghosts
    ghosts.forEach((g, i) => {
      const gx = g.drawX * TILE + TILE / 2;
      const gy = g.drawY * TILE + TILE / 2;
      const r = TILE / 2 - 2;
      ctx.fillStyle = g.mode === "frightened" ? COLORS.frightened : COLORS.ghost[i];
      ctx.beginPath();
      ctx.arc(gx, gy - 2, r, Math.PI, 0);
      ctx.lineTo(gx + r, gy + r - 2);
      // wavy bottom
      ctx.lineTo(gx + r / 2, gy + r / 2);
      ctx.lineTo(gx, gy + r - 2);
      ctx.lineTo(gx - r / 2, gy + r / 2);
      ctx.lineTo(gx - r, gy + r - 2);
      ctx.closePath();
      ctx.fill();
      // eyes
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(gx - 4, gy - 3, 2.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(gx + 4, gy - 3, 2.5, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#1e3a8a";
      ctx.beginPath(); ctx.arc(gx - 4, gy - 3, 1.2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(gx + 4, gy - 3, 1.2, 0, Math.PI * 2); ctx.fill();
    });

    // HUD
    ctx.fillStyle = "#e5e7eb";
    ctx.font = "12px monospace";
    ctx.fillText(`SCORE ${score}   LIVES ${lives}   LEVEL ${level}`, 6, 14);
    if (!running && lives <= 0) {
      ctx.fillStyle = "rgba(15, 23, 42, 0.88)";
      ctx.fillRect(0, HEIGHT / 2 - 34, WIDTH, 48);
      ctx.fillStyle = "#ef4444";
      ctx.font = "bold 18px monospace";
      ctx.fillText("GAME OVER  —  press R to play again", 18, HEIGHT / 2 - 8);
    }
  }

  function loop() {
    update();
    draw();
    raf = requestAnimationFrame(loop);
  }

  function start() {
    if (raf) cancelAnimationFrame(raf);
    resetState(false);
    running = true;
    raf = requestAnimationFrame(loop);
  }

  const keyHandler = (e) => {
    const map = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", w: "up", s: "down", a: "left", d: "right" };
    if (map[e.key]) {
      e.preventDefault();
      player.next = map[e.key];
    }
    if (e.key === "r" || e.key === "R") start();
  };

  // ---- mobile controls: swipe anywhere on the canvas ----
  let touchStart = null;
  const touchStartHandler = (e) => {
    touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const touchMoveHandler = (e) => {
    // stop the page from scrolling while the player swipes
    e.preventDefault();
  };
  const touchEndHandler = (e) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    touchStart = null;
    // require a minimum swipe distance so taps don't move the player
    if (Math.abs(dx) < 24 && Math.abs(dy) < 24) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      player.next = dx > 0 ? "right" : "left";
    } else {
      player.next = dy > 0 ? "down" : "up";
    }
  };

  window.addEventListener("keydown", keyHandler);
  canvas.addEventListener("touchstart", touchStartHandler, { passive: true });
  canvas.addEventListener("touchmove", touchMoveHandler, { passive: false });
  canvas.addEventListener("touchend", touchEndHandler, { passive: true });
  start();

  return {
    setDirection(name) {
      if (DIRS[name]) player.next = name;
    },
    restart: start,
    destroy() {
      window.removeEventListener("keydown", keyHandler);
      canvas.removeEventListener("touchstart", touchStartHandler);
      canvas.removeEventListener("touchmove", touchMoveHandler);
      canvas.removeEventListener("touchend", touchEndHandler);
      if (raf) cancelAnimationFrame(raf);
    },
  };
}
